import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import MimeType from "../../../document/mimetype/MimeType";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	FormDocument,
	Functions,
	Information,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	ToolboxFormsForms
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

export class FormsFlattenDialogDefinition extends PortalDialog<ToolboxFormsForms,
	PortalDialogProperties<ToolboxFormsForms>, PortalDialogState<ToolboxFormsForms>> {
	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.editForms, Functions.getEditFormsDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.FLATTEN_FORMS, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}

			if (FunctionHelper.applyValue(
				selectedDocument.metadata?.form?.field, FormDocument.getFieldDefault()
			).length === 0) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["flatten-forms-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.flatten_forms.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.flatten_forms.flatten"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<fieldset className="p-col-12 p-grid">
						<div className="dialog-message">
							<Translate {...this.props} data="flatten_forms.message" namespace="dialog"/>
						</div>
					</fieldset>
				</div>
			</Dialog>
		);
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-flatten"}/>
					<Translate {...this.props} data="flatten_forms.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModify, Security.getCanModifyDefault()
		);
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			forms: this.state.parameter!.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.forms.flatten", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.FLATTEN_FORMS;
	}

	protected getDefaultParameter(): Parameter<ToolboxFormsForms> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const FormsFlattenDialog = React.memo(FormsFlattenDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<ToolboxFormsForms>>,
		nextProps: Readonly<PortalDialogProperties<ToolboxFormsForms>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default FormsFlattenDialog;